/* @jsx h */
import { defineComponent, ref, h, onMounted, computed } from "vue";
import { setTitle } from "@lemmonhq/orders";
import { useToast } from "@app/vue/providers/Toast";
import { taxSettingsAPI } from "@app/data/api/tax_settings";
import { useLocalization, Loading, ErrorRenderer } from "@lemmonhq/uikit";
import type { TaxItemType, TaxProfile, TaxRate } from "@lemmonhq/types";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import { RatesTab } from "./RatesTab";
import { ProfilesTab } from "./ProfilesTab";
import { ItemTypesTab } from "./ItemTypesTab";

type TabId = "rates" | "profiles" | "items";

const tabs: Array<{
  id: TabId;
  label: string;
  icon: string;
  description: string;
}> = [
  {
    id: "rates",
    label: "Tax rates",
    icon: "show_chart",
    description:
      "Define percentages and effective periods for each jurisdiction.",
  },
  {
    id: "profiles",
    label: "Profiles",
    icon: "badge",
    description:
      "Map rates to service flows and product types for automatic tax handling.",
  },
  {
    id: "items",
    label: "Item types",
    icon: "category",
    description:
      "Group products into tax item types to unlock precise profile targeting.",
  },
];

export const TaxSettings = defineComponent({
  name: "TaxSettings",
  setup() {
    const toast = useToast();
    const { t } = useLocalization();
    const activeTab = ref<TabId>("rates");

    const taxRates = ref<TaxRate[]>([]);
    const taxProfiles = ref<TaxProfile[]>([]);
    const taxItemTypes = ref<TaxItemType[]>([]);

    const loading = ref(false);
    const loadError = ref(false);

    const loadAll = async () => {
      loading.value = true;
      loadError.value = false;
      try {
        const [rates, profiles, itemTypes] = await Promise.all([
          taxSettingsAPI.getTaxRates(),
          taxSettingsAPI.getTaxProfiles(),
          taxSettingsAPI.getTaxItemTypes(),
        ]);
        taxRates.value = rates;
        taxProfiles.value = profiles;
        taxItemTypes.value = itemTypes;
      } catch (error) {
        console.error("Failed to load tax settings", error);
        loadError.value = true;
        toast.showToast("Unable to load tax settings. Please retry.", {
          type: "error",
        });
      } finally {
        loading.value = false;
      }
    };

    const refreshRates = async () => {
      const [rates, profiles] = await Promise.all([
        taxSettingsAPI.getTaxRates(),
        taxSettingsAPI.getTaxProfiles(),
      ]);
      taxRates.value = rates;
      taxProfiles.value = profiles;
    };

    const refreshProfiles = async () => {
      const [profiles, rates] = await Promise.all([
        taxSettingsAPI.getTaxProfiles(),
        taxSettingsAPI.getTaxRates(),
      ]);
      taxProfiles.value = profiles;
      taxRates.value = rates;
    };

    const refreshItemTypes = async () => {
      const [itemTypes, profiles] = await Promise.all([
        taxSettingsAPI.getTaxItemTypes(),
        taxSettingsAPI.getTaxProfiles(),
      ]);
      taxItemTypes.value = itemTypes;
      taxProfiles.value = profiles;
    };

    onMounted(async () => {
      setTitle(t("general.page_titles.tax_settings"));
      await loadAll();
    });

    const activeTabMeta = computed(
      () => tabs.find((tab) => tab.id === activeTab.value)!,
    );

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader
          icon="functions"
          title="Tax management"
          description="Control tax rates, assignment profiles, and item type catalogues. Changes sync instantly to every POS and online channel."
        >
          {{
            actions: () => (
              <div class="flex items-center gap-1 rounded-xl border  bg-base-200/60 p-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    class={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                      activeTab.value === tab.id
                        ? "bg-base-100 text-primary shadow-sm"
                        : "text-base-content/60 hover:text-base-content"
                    }`}
                    onClick={() => (activeTab.value = tab.id)}
                  >
                    <span class="material-symbols-outlined text-sm">
                      {tab.icon}
                    </span>
                    {tab.label}
                  </button>
                ))}
              </div>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent noPadding>
            {loading.value && (
              <div class="flex h-80 items-center justify-center">
                <Loading />
              </div>
            )}

            {!loading.value && loadError.value && (
              <ErrorRenderer
                error={null}
                message="Failed to load tax settings."
                onRetry={() => void loadAll()}
              />
            )}

            {!loading.value && !loadError.value && (
              <>
                <div class="border-b border-base-content/5 bg-base-200/30 px-4 py-3">
                  <p class="text-sm text-base-content/60">
                    {activeTabMeta.value.description}
                  </p>
                </div>

                {activeTab.value === "rates" && (
                  <RatesTab rates={taxRates.value} onRefresh={refreshRates} />
                )}
                {activeTab.value === "profiles" && (
                  <ProfilesTab
                    profiles={taxProfiles.value}
                    taxItemTypes={taxItemTypes.value}
                    onRefresh={refreshProfiles}
                  />
                )}
                {activeTab.value === "items" && (
                  <ItemTypesTab
                    itemTypes={taxItemTypes.value}
                    onRefresh={refreshItemTypes}
                  />
                )}
              </>
            )}
          </AppSubpageContent>
        </AppSubpage>
      </div>
    );
  },
});
